"use client";

import { type useBuyBoxFactory, useSelection } from "@trydig/centra/client";
import { DialogClose } from "@trydig/ui";
import { useActionState } from "react";
import { XIcon } from "@/components/icons/XIcon";
import { Translate } from "@/components/Translate";
import { Button } from "@/components/ui/Button";
import {
	ModalContent,
	ModalMain,
	ModalRoot,
	ModalTrigger,
	ModalViewport,
} from "@/components/ui/Drawer";
import { IconButton } from "@/components/ui/IconButton";
import { TextField } from "@/components/ui/Input";
import { useTranslations } from "@/i18n/client";
import { useKlaviyoBackInStock } from "@/lib/hooks/useKlaviyoProfile";
import styles from "./AddToCartButton.module.css";

type BuyBox = ReturnType<typeof useBuyBoxFactory>;

interface BackInStockProps {
	getButtonProps: BuyBox["getButtonProps"];
	// item: BuyBox["item"];
	item: string | null;
	fullWidth?: boolean;
}

type BackInStockState = {
	success?: boolean;
	error?: string;
};

export const BackInStock = ({
	getButtonProps,
	item,
	fullWidth,
}: BackInStockProps) => {
	const backInStock = useKlaviyoBackInStock();
	const t = useTranslations();

	const { data: loggedInEmail } = useSelection({
		select: (data) => data.loggedIn?.email,
	});

	const [state, action, pending] = useActionState(
		async (_: BackInStockState, data: FormData): Promise<BackInStockState> => {
			const email = data.get("email")?.toString();

			if (!item || !email) {
				return { error: t("Invalid values submitted") };
			}

			return backInStock
				.mutateAsync({
					email,
					externalVariantId: item,
				})
				.then(() => ({ success: true }))
				.catch(() => ({
					error: t("Something went wrong submitting your request"),
				}));
		},
		{} as BackInStockState,
	);

	if (!item) return null;

	return (
		<ModalRoot>
			<ModalTrigger asChild>
				<Button fullWidth={fullWidth} {...getButtonProps()}>
					<Translate>Notify me</Translate>
				</Button>
			</ModalTrigger>

			<ModalViewport style={{ minHeight: "0px" }}>
				<ModalContent>
					<ModalMain className={styles.backInStockContent}>
						<DialogClose className={styles.close} asChild>
							<IconButton>
								<XIcon />
							</IconButton>
						</DialogClose>
						<header>
							<h2>
								<Translate>Notify me</Translate>
							</h2>
							<span>
								<Translate>
									Fill in your email and we will let you know if this item gets
									restocked.
								</Translate>
							</span>
						</header>

						<form className={styles.backInStock} action={action}>
							{!state.success && (
								<TextField
									key={loggedInEmail}
									defaultValue={loggedInEmail}
									type="email"
									required
									name="email"
									label={t("Email")}
								/>
							)}
							{state.error && <p className={styles.error}>{state.error}</p>}
							{state.success && (
								<p className={styles.success}>
									<Translate>You have successfully subscribed!</Translate>
								</p>
							)}

							{state.success ? (
								<DialogClose asChild>
									<Button type="button">
										<Translate>Close</Translate>
									</Button>
								</DialogClose>
							) : (
								<Button loading={pending} type="submit">
									<Translate>Notify me</Translate>
								</Button>
							)}
						</form>
					</ModalMain>
				</ModalContent>
			</ModalViewport>
		</ModalRoot>
	);
};
